import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, db } from '../firebase';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Github, Mail, Sparkles, Layers, Zap, Palette, Brush, LogIn, UserPlus, Loader2, Anchor, Code2, ExternalLink, Store, ShieldCheck, ChevronDown } from 'lucide-react';
import { createCheckoutSession } from '../lib/stripe';
import { toast, Toaster } from 'sonner';
import Hero from '../components/Hero';
import { YachtLogo } from '../components/YachtLogo';
import Navbar from '../components/Navbar';
import SupportForm from '../components/SupportForm';

export default function LandingPage() {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeMagnet, setActiveMagnet] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [deployments, setDeployments] = useState<any[]>([]);
  const [isLoadingDeployments, setIsLoadingDeployments] = useState(true);
  const [deploymentsError, setDeploymentsError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const fetchDeployments = async () => {
      try {
        const q = query(
          collection(db, 'deployments'),
          orderBy('deployedAt', 'desc'),
          limit(6)
        );
        const snapshot = await getDocs(q);
        let deps: any[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Fallback to featured deployments if none found in DB
        if (deps.length === 0) {
          deps = [
            { id: 'saas-starter', title: 'SaaS Alpha Engine', description: 'A complete multi-tenant SaaS architecture synthesized in 14 minutes.', status: 'Production', deployedAt: { seconds: Date.now() / 1000 - 86400 } },
            { id: 'ecomm-stack', title: 'headless_commerce', description: 'Next-gen commerce synthesis with automated inventory kinetics.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 172800 } },
            { id: 'ai-dashboard', title: 'Neural Monitor V2', description: 'Real-time inference tracking with acoustics-ready visualizers.', status: 'Online', deployedAt: { seconds: Date.now() / 1000 - 259200 } },
            { id: 'social-graph', title: 'Nexus Protocol', description: 'Autonomous social graph with identity synthesis layers.', status: 'Active', deployedAt: { seconds: Date.now() / 1000 - 345600 } },
            { id: 'fin-bridge', title: 'Liquid Ledger', description: 'High-frequency transaction synthesis and audit logic.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 432000 } },
            { id: 'asset-forge', title: 'Genesis Builder', description: 'The foundation for multi-modal asset synthesis and deployment.', status: 'Ready', deployedAt: { seconds: Date.now() / 1000 - 518400 } },
          ];
        }
        
        setDeployments(deps);
      } catch (err: any) {
        console.error('Error fetching gallery deployments:', err);
        // Fallback to featured on error
        setDeployments([
          { id: 'saas-starter', title: 'SaaS Alpha Engine', description: 'A complete multi-tenant SaaS architecture synthesized in 14 minutes.', status: 'Production', deployedAt: { seconds: Date.now() / 1000 - 86400 } },
          { id: 'ecomm-stack', title: 'headless_commerce', description: 'Next-gen commerce synthesis with automated inventory kinetics.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 172800 } },
          { id: 'ai-dashboard', title: 'Neural Monitor V2', description: 'Real-time inference tracking with acoustics-ready visualizers.', status: 'Online', deployedAt: { seconds: Date.now() / 1000 - 259200 } },
        ]);
      } finally {
        setIsLoadingDeployments(false);
      }
    };

    fetchDeployments();
  }, []);

  const handleUpgrade = async (plan: string = 'pro') => {
    if (!auth.currentUser) {
      toast.error('Please sign in to upgrade.');
      return;
    }

    setIsUpgrading(true);
    try {
      await createCheckoutSession('', auth.currentUser.uid, plan);
    } catch (error: any) {
      toast.error('Checkout failed', {
        description: error.message || 'The checkout engine encountered a capacity error.'
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoginError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('Login popup closed by user');
        return;
      }
      console.error('Login failed:', error);
      if (error.code === 'auth/unauthorized-domain') {
        setLoginError('Domain not authorized. Please add yacht-labs.com to "Authorized domains" in Firebase Console > Authentication > Settings.');
      } else {
        setLoginError('Login failed. Please check your connection or try again.');
      }
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoading(true);

    if (!email) {
      setLoginError('Please enter your email address.');
      setIsLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent. Please check your inbox.');
      setIsResetPassword(false);
    } catch (error: any) {
      console.error('Password reset failed:', error);
      setLoginError(error.message || 'Failed to send password reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoading(true);

    if (!email || !password) {
      setLoginError('Please enter both email and password.');
      setIsLoading(false);
      return;
    }

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error: any) {
      console.error('Email auth failed:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setLoginError('This email is already in use. Try signing in instead.');
          break;
        case 'auth/invalid-email':
          setLoginError('Invalid email address.');
          break;
        case 'auth/weak-password':
          setLoginError('Password should be at least 6 characters.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setLoginError('Invalid email or password.');
          break;
        default:
          setLoginError('Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const assembleHtml = (mainContent: string, allFiles: any[]) => {
    if (!mainContent || !mainContent.trim().toLowerCase().startsWith('<!doctype html>') && !mainContent.trim().toLowerCase().startsWith('<html')) {
      return mainContent || '';
    }

    let assembed = mainContent;

    if (Array.isArray(allFiles)) {
      // Inline CSS
      allFiles.forEach(file => {
        if (file.name.endsWith('.css')) {
          const linkTag = new RegExp(`<link[^>]*href=["'](?:./)?${file.name}["'][^>]*>`, 'gi');
          if (linkTag.test(assembed)) {
            assembed = assembed.replace(linkTag, `<style>${file.content}</style>`);
          } else {
            assembed = assembed.replace('</head>', `<style>${file.content}</style></head>`);
          }
        }
      });

      // Inline JS
      allFiles.forEach(file => {
        if (file.name.endsWith('.js')) {
          const scriptTag = new RegExp(`<script[^>]*src=["'](?:./)?${file.name}["'][^>]*>\\s*</script>`, 'gi');
          if (scriptTag.test(assembed)) {
            assembed = assembed.replace(scriptTag, `<script>${file.content}</script>`);
          } else {
            assembed = assembed.replace('</body>', `<script>${file.content}</script></body>`);
          }
        }
      });
    }

    return assembed;
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <Hero />

      {/* Social Proof Bar */}
      <section className="px-8 py-12 bg-black border-y border-zinc-900 border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8 opacity-40 grayscale hover:grayscale-0 transition-all">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Architecture trusted by founders at</span>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16">
            <div className="flex items-center gap-2">
              <img src="https://svgl.app/library/vercel.svg" alt="Vercel" className="h-5 w-auto invert" referrerPolicy="no-referrer" />
              <span className="font-mono text-sm font-bold tracking-tighter uppercase whitespace-nowrap">Vercel</span>
            </div>
            <div className="flex items-center gap-2">
              <img src="https://svgl.app/library/stripe.svg" alt="Stripe" className="h-5 w-auto" referrerPolicy="no-referrer" />
              <span className="font-mono text-sm font-bold tracking-tighter uppercase whitespace-nowrap">Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <img src="https://svgl.app/library/supabase.svg" alt="Supabase" className="h-5 w-auto" referrerPolicy="no-referrer" />
              <span className="font-mono text-sm font-bold tracking-tighter uppercase whitespace-nowrap">Supabase</span>
            </div>
            <div className="flex items-center gap-2">
              <img src="https://svgl.app/library/prisma.svg" alt="Prisma" className="h-5 w-auto invert" referrerPolicy="no-referrer" />
              <span className="font-mono text-sm font-bold tracking-tighter uppercase whitespace-nowrap">Prisma</span>
            </div>
          </div>
        </div>
      </section>

      {/* Community Gallery Section */}
      <section id="gallery" className="px-8 py-32 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">Community</h2>
            <h3 className="text-4xl font-mono uppercase tracking-tighter">Ship Gallery</h3>
            <p className="text-zinc-400 font-light max-w-2xl mx-auto mt-6">
              Explore the latest synthesized applications and components deployed by the Yacht Labs community.
            </p>
          </div>
          
          {isLoadingDeployments ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-8 h-8 border-2 border-zinc-800 border-t-zinc-400 rounded-full animate-spin" />
            </div>
          ) : deploymentsError ? (
            <div className="text-center py-16 border border-dashed border-red-900/50 rounded-3xl bg-red-950/20 mb-16">
              <h3 className="text-xl font-mono uppercase tracking-tight text-red-400 mb-2">Error Loading Builds</h3>
              <p className="text-red-500/70 font-light max-w-md mx-auto">{deploymentsError}</p>
            </div>
          ) : deployments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {deployments.map((deployment, index) => (
                <motion.div
                  key={deployment.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative bg-zinc-900/30 border border-zinc-800 rounded-2xl overflow-hidden hover:border-zinc-600 transition-all flex flex-col"
                >
                  <div className="aspect-video bg-zinc-950 border-b border-zinc-800 relative overflow-hidden">
                    {deployment.coverImageUrl ? (
                      <img 
                        src={deployment.coverImageUrl} 
                        alt={deployment.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                      />
                    ) : deployment.content ? (
                      <iframe
                        srcDoc={assembleHtml(deployment.content, deployment.files)}
                        title={deployment.title}
                        className="absolute top-0 left-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none bg-zinc-950 transition-opacity duration-700"
                        sandbox="allow-scripts"
                        tabIndex={-1}
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity">
                          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]" />
                        </div>
                        <Code2 className="text-zinc-800 group-hover:text-zinc-600 transition-colors z-10" size={48} />
                      </div>
                    )}
                    <div className="absolute inset-0 z-10" />
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-mono uppercase tracking-tight truncate pr-4 text-white">
                        {deployment.title || 'Untitled Build'}
                      </h3>
                      <a 
                        href={`/pub/${deployment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-white transition-colors shrink-0 z-20 relative"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                    
                    <p className="text-sm text-zinc-500 font-light line-clamp-2 mb-6 flex-1">
                      {deployment.description || 'No description provided for this build.'}
                    </p>
                    
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-zinc-800/50">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                        {deployment.deployedAt ? new Date(deployment.deployedAt.seconds * 1000).toLocaleDateString() : 'Unknown Date'}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/70 bg-emerald-500/10 px-2 py-1 rounded-full">
                        {deployment.status || 'Live'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-zinc-800 rounded-3xl mb-16">
              <Layers className="mx-auto text-zinc-600 mb-4" size={32} />
              <p className="text-zinc-600 font-light">No published builds found.</p>
            </div>
          )}

          <div className="text-center mt-8 space-x-4">
            <Link 
              to="/gallery"
              className="inline-flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
            >
              View All Published Builds <ArrowRight size={16} />
            </Link>
            <Link 
              to="/marketplace"
              className="inline-flex items-center justify-center gap-2 text-sm font-mono uppercase tracking-widest text-[#0074D9] hover:text-[#3399FF] transition-colors"
            >
              Browse Marketplace <Store size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Free Samples Section */}
      <section className="px-8 py-32 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">Free Samples</h2>
            <h3 className="text-4xl font-mono uppercase tracking-tighter">Synthesis Lead Magnets</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-zinc-900/30 border border-zinc-800 p-12 rounded-[48px] flex flex-col justify-between group hover:border-zinc-700 transition-all">
              <div>
                <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-zinc-700 transition-colors">
                  <Layers className="text-zinc-400" size={32} />
                </div>
                <h4 className="text-2xl font-mono uppercase mb-4">SaaS Landing Page Generator</h4>
                <p className="text-zinc-500 font-light leading-relaxed mb-8">
                  Generate a high-conversion landing page brief and structure for your next SaaS idea in seconds.
                </p>
              </div>
              <button 
                onClick={() => setActiveMagnet('SaaS Landing Page Generator')}
                className="flex items-center gap-2 text-zinc-100 font-mono uppercase text-xs tracking-widest group-hover:gap-4 transition-all"
              >
                Access Tool <ArrowRight size={16} />
              </button>
            </div>

            <div className="bg-zinc-900/30 border border-zinc-800 p-12 rounded-[48px] flex flex-col justify-between group hover:border-zinc-700 transition-all">
              <div>
                <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-zinc-700 transition-colors">
                  <Zap className="text-zinc-400" size={32} />
                </div>
                <h4 className="text-2xl font-mono uppercase mb-4">Project Scaffolder</h4>
                <p className="text-zinc-500 font-light leading-relaxed mb-8">
                  Instantly generate a project roadmap, tech stack recommendation, and initial task list.
                </p>
              </div>
              <button 
                onClick={() => setActiveMagnet('Project Scaffolder')}
                className="flex items-center gap-2 text-zinc-100 font-mono uppercase text-xs tracking-widest group-hover:gap-4 transition-all"
              >
                Access Tool <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Modal */}
      {activeMagnet && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setActiveMagnet(null)} />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-zinc-900 border border-zinc-800 p-12 rounded-[48px] max-w-lg w-full text-center space-y-8"
          >
            <div className="w-20 h-20 bg-zinc-800 rounded-3xl flex items-center justify-center mx-auto">
              <Sparkles className="text-zinc-100" size={40} />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-mono uppercase tracking-tighter">Sign up to use the {activeMagnet}</h3>
              <p className="text-zinc-500 font-light text-sm">Join Yacht Labs to access our suite of synthesis tools and start building your vision today.</p>
            </div>
            <div className="space-y-4">
              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-white text-black rounded-xl text-sm font-bold uppercase tracking-widest transition-all hover:scale-[1.02]"
              >
                Continue with Google
              </button>
              {loginError && (
                <p className="text-[10px] text-red-500 font-mono uppercase tracking-wider">{loginError}</p>
              )}
              <button 
                onClick={() => {
                  setActiveMagnet(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-zinc-500 font-mono uppercase text-[10px] tracking-widest hover:text-zinc-300 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Features Grid */}
      <section className="px-8 py-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border-b border-zinc-800">
        {[
          { title: 'Think', desc: 'AI-driven task management and document synthesis. Plan your next move with intelligence.', icon: Sparkles },
          { title: 'Build', desc: 'The Assembler. Turn raw, messy research into investor decks or landing pages.', icon: Layers },
          { title: 'Ship', desc: 'The Engine. Automation and deployment layer. Ideas are static, Ship makes them move.', icon: Zap },
          { title: 'Edit', desc: 'The editor. Visual drag-and-drop builder to craft your presence without shifting pixels.', icon: Palette },
          { title: 'Design', desc: 'The Identity Lab. Architect your system primitives: from typography scales to reactive color theories.', icon: Brush },
          { title: 'Documentation', desc: 'Real-time collaborative documentation and planning.', icon: ArrowRight },
        ].map((f, i) => (
          <div key={i} className="bg-black p-12 hover:bg-zinc-950 transition-colors group cursor-default">
            <f.icon className="mb-8 opacity-40 group-hover:opacity-100 transition-opacity" size={32} />
            <h3 className="text-2xl font-mono uppercase mb-4">{f.title}</h3>
            <p className="text-zinc-500 font-light leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="px-8 py-32 bg-black">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">The Lab Access</h2>
          <h3 className="text-5xl font-mono uppercase tracking-tighter mb-16">Choose Your Velocity</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Tier */}
            <div className="bg-zinc-900/30 border border-zinc-800 p-8 md:p-10 rounded-[40px] text-left flex flex-col justify-between">
              <div>
                <h4 className="text-lg font-mono uppercase mb-2">Free Tier</h4>
                <p className="text-3xl font-mono mb-6">$0 <span className="text-sm text-zinc-600">/ forever</span></p>
                <ul className="space-y-3 mb-8 text-xs text-zinc-400 font-light">
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-zinc-600" /> 1 Active Project</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-zinc-600" /> Basic Synthesis</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-zinc-600" /> Manual Kinetics (Ship)</li>
                  <li className="flex items-center gap-2 text-zinc-700 line-through"><ArrowRight size={10} /> Real-time Mission Control</li>
                </ul>
              </div>
              <button 
                onClick={handleGoogleLogin}
                className="w-full py-3.5 border border-zinc-800 rounded-xl font-mono uppercase text-[10px] tracking-widest hover:bg-zinc-800 transition-colors"
              >
                Get Started
              </button>
            </div>

            {/* Founder Access (Limited) */}
            <div className="bg-[#0074D9] text-white p-8 md:p-10 rounded-[40px] text-left relative overflow-hidden flex flex-col justify-between shadow-[0_0_50px_rgba(0,116,217,0.2)]">
              <div className="absolute -top-4 -right-4 opacity-10">
                <Anchor size={120} />
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-lg font-mono uppercase">Founder Batch 01</h4>
                  <span className="px-2 py-0.5 bg-white/20 rounded text-[8px] font-mono uppercase tracking-widest font-bold">42 Seats Left</span>
                </div>
                <p className="text-3xl font-mono mb-2">$29 <span className="text-sm opacity-60">/ first month</span></p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-white/60 mb-6">Claim your founders discount. $70 off your first month of Pro.</p>
                <ul className="space-y-3 mb-8 text-xs font-medium">
                  <li className="flex items-center gap-2"><ArrowRight size={10} /> Unlimited MVP Synthesis</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} /> Early Access to Architecture Engine</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} /> Custom Founder Certificate (.svg)</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} /> Discord Captains Lounge Access</li>
                </ul>
              </div>
              <div className="space-y-4 relative z-10">
                <button 
                  onClick={() => {
                    if (!auth.currentUser) handleGoogleLogin();
                    else handleUpgrade('founder');
                  }}
                  disabled={isUpgrading}
                  className="w-full py-4 bg-white text-[#0074D9] rounded-xl font-mono uppercase text-[10px] tracking-widest font-bold hover:bg-zinc-100 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isUpgrading ? <Loader2 className="animate-spin" size={14} /> : 'Secure My Entry'}
                </button>
                <div className="flex items-center justify-center gap-2 opacity-60">
                   <ShieldCheck size={10} />
                   <span className="text-[8px] font-mono uppercase tracking-widest">7-Day Refund Policy / Secure via Stripe</span>
                </div>
              </div>
            </div>

            {/* Pro Tier */}
            <div className="bg-zinc-100 text-black p-8 md:p-10 rounded-[40px] text-left relative overflow-hidden flex flex-col justify-between border-4 border-[#0074D9]/20">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="text-lg font-mono uppercase">Pro Lab</h4>
                  <span className="bg-[#0074D9] text-white text-[8px] font-mono px-2 py-1 rounded">MOST POPULAR</span>
                </div>
                <p className="text-3xl font-mono mb-6">$99 <span className="text-sm opacity-50">/ month</span></p>
                <ul className="space-y-3 mb-8 text-xs font-medium">
                  <li className="flex items-center gap-2 font-bold"><ArrowRight size={10} className="text-[#0074D9]" /> UNLIMITED Projects & Hosting</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-[#0074D9]" /> 1,000 AI Synthesis Builds /mo</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-[#0074D9]" /> 5,000 Visual Logic Edits /mo</li>
                  <li className="flex items-center gap-2 font-bold"><ArrowRight size={10} className="text-[#0074D9]" /> ALL Marketplace Assets FREE</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-[#0074D9]" /> Advanced Autopilot Engine</li>
                  <li className="flex items-center gap-2"><ArrowRight size={10} className="text-[#0074D9]" /> Real-time Mission Control</li>
                </ul>
              </div>
              <button 
                onClick={() => {
                  if (!auth.currentUser) handleGoogleLogin();
                  else handleUpgrade('pro');
                }}
                disabled={isUpgrading}
                className="w-full py-4 bg-[#0074D9] text-white rounded-xl font-mono uppercase text-[10px] tracking-widest font-bold hover:bg-[#0074D9]/90 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isUpgrading ? <Loader2 className="animate-spin" size={14} /> : 'Scale to Pro'}
              </button>
            </div>
          </div>
          
          {/* Trust Assets */}
          <div className="mt-16 pt-16 border-t border-zinc-900 grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <Zap size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Instant Fulfillment</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Synthesis begins immediately upon authentication.</p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <ShieldCheck size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Founder Guarantee</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Not impressed? 100% refund within 7 days. No questions.</p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-900 flex items-center justify-center text-[#0074D9]">
                <Layers size={20} />
              </div>
              <h5 className="text-[10px] font-mono uppercase tracking-widest">Clean Architecture</h5>
              <p className="text-[9px] text-zinc-500 uppercase tracking-widest leading-relaxed">Your data is yours. Export full source code anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section / About */}
      <section id="vision" className="px-8 py-32 bg-zinc-950 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
          <h2 className="text-xs font-mono uppercase tracking-[0.4em] text-zinc-600 mb-8">From the Bridge</h2>
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[#0074D9] mb-8 bg-zinc-900 shadow-[0_0_20px_rgba(0,116,217,0.3)]">
            <img 
              src="/headshot.jpg" 
              alt="Cheryl Qian" 
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all"
              onError={(e) => {
                // Fallback to original if file not found locally
                e.currentTarget.src = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800&h=800";
              }}
              referrerPolicy="no-referrer"
            />
          </div>
          <h3 className="text-3xl font-mono uppercase tracking-tight mb-8">Building for the Builders</h3>
          <p className="text-zinc-400 font-light leading-relaxed text-lg mb-12 italic">
            "Software creation shouldn't be a privilege of the few or a friction-filled journey for the many. Yacht Labs exists to democratize 'dangerous' developer capability—shortening the loop from intent to existence until there's no friction left between having an idea and having a product."
          </p>
          <div className="flex flex-col items-center">
            <span className="font-mono text-xs uppercase tracking-widest text-white">Cheryl Qian</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#0074D9] mt-1">Founder & CEO</span>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="px-8 py-32 bg-[#050505] border-t border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-xs font-mono uppercase tracking-[0.3em] text-[#0074D9] mb-4">Insights for Builders</h2>
            <h3 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter text-white mb-4">
              Tactical breakdowns on building,<br className="hidden md:block" /> shipping, and scaling products fast.
            </h3>
          </div>

          {/* Featured Post */}
          <div className="mb-8">
            <Link to="/blog/build-saas-in-2-hours" className="group block relative bg-zinc-900/30 border border-zinc-800 rounded-[32px] overflow-hidden hover:border-[#0074D9]/50 transition-all">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />
              <img 
                src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=1200&h=600" 
                alt="Code on a screen" 
                className="w-full h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
                referrerPolicy="no-referrer"
              />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 z-20">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-[#0074D9]/20 text-[#0074D9] border border-[#0074D9]/30 rounded-full text-[10px] font-mono uppercase tracking-widest">
                    Build in Public
                  </span>
                  <span className="text-xs font-mono text-zinc-400">4 min read</span>
                </div>
                <h4 className="text-3xl md:text-4xl font-mono uppercase tracking-tight text-white mb-4 group-hover:text-[#0074D9] transition-colors">
                  How I Built a SaaS in 2 Hours Using Yacht Labs
                </h4>
                <p className="text-zinc-400 font-light max-w-2xl mb-6 text-lg">
                  Most people spend weeks planning a product. I built one in 2 hours. Here's exactly how it worked.
                </p>
                <div className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white group-hover:text-[#0074D9] transition-colors">
                  Read Case Study <ArrowRight size={16} />
                </div>
              </div>
            </Link>
          </div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Post 2 */}
            <Link to="/blog/idea-to-product-fast" className="group bg-zinc-900/30 border border-zinc-800 rounded-3xl overflow-hidden hover:border-zinc-600 hover:shadow-[0_0_30px_rgba(255,255,255,0.02)] transition-all flex flex-col">
              <div className="h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800&h=400" 
                  alt="Analytics dashboard" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-[10px] font-mono uppercase tracking-widest">
                    Growth
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">3 min read</span>
                </div>
                <h4 className="text-xl font-mono uppercase tracking-tight text-white mb-3 group-hover:text-zinc-300 transition-colors">
                  From Idea to Live Product in One Afternoon
                </h4>
                <p className="text-zinc-500 font-light text-sm mb-6 flex-1">
                  Most ideas never become products. Not because they're bad. Because they never get built. Here's a different approach.
                </p>
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 group-hover:text-white transition-colors mt-auto">
                  Read More <ArrowRight size={14} />
                </div>
              </div>
            </Link>

            {/* Post 3 */}
            <Link to="/blog/validate-startup-fast" className="group bg-zinc-900/30 border border-zinc-800 rounded-3xl overflow-hidden hover:border-zinc-600 hover:shadow-[0_0_30px_rgba(255,255,255,0.02)] transition-all flex flex-col">
              <div className="h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=400" 
                  alt="Data visualization" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-[10px] font-mono uppercase tracking-widest">
                    Playbook
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">5 min read</span>
                </div>
                <h4 className="text-xl font-mono uppercase tracking-tight text-white mb-3 group-hover:text-zinc-300 transition-colors">
                  The Fastest Way to Validate a Startup in 2026
                </h4>
                <p className="text-zinc-500 font-light text-sm mb-6 flex-1">
                  Validation doesn't come from thinking. It comes from exposure. Here is the fastest way to get there.
                </p>
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 group-hover:text-white transition-colors mt-auto">
                  Read More <ArrowRight size={14} />
                </div>
              </div>
            </Link>

            {/* Post 4 */}
            <Link to="/blog/why-builders-never-ship" className="group bg-zinc-900/30 border border-zinc-800 rounded-3xl overflow-hidden hover:border-zinc-600 hover:shadow-[0_0_30px_rgba(255,255,255,0.02)] transition-all flex flex-col">
              <div className="h-48 overflow-hidden relative">
                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
                <img 
                  src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&q=80&w=800&h=400" 
                  alt="Laptop on desk" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2 py-1 bg-zinc-800 text-zinc-300 rounded text-[10px] font-mono uppercase tracking-widest">
                    Contrarian
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">3 min read</span>
                </div>
                <h4 className="text-xl font-mono uppercase tracking-tight text-white mb-3 group-hover:text-zinc-300 transition-colors">
                  Why Most Builders Never Ship (And How to Fix It)
                </h4>
                <p className="text-zinc-500 font-light text-sm mb-6 flex-1">
                  Stop learning design. Start shipping products. The real reason you haven't shipped anything is your speed.
                </p>
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-400 group-hover:text-white transition-colors mt-auto">
                  Read More <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          </div>

          {/* CTA Footer */}
          <div className="text-center bg-zinc-900/50 border border-zinc-800 rounded-[32px] p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,116,217,0.1),transparent_50%)]" />
            <div className="relative z-10">
              <h4 className="text-3xl font-mono uppercase tracking-tighter text-white mb-6">Stop reading. Start shipping.</h4>
              <Link 
                to="/signup"
                className="px-8 py-4 bg-white text-black rounded-xl font-mono uppercase text-sm tracking-widest font-bold hover:bg-zinc-200 hover:scale-[1.02] transition-all inline-flex items-center gap-2"
              >
                Get Started <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="px-8 py-32 bg-black">
        <div className="max-w-6xl mx-auto">
          <SupportForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="p-12 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start gap-12">
          <div className="space-y-6 max-w-xs">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <YachtLogo className="w-6 h-6" />
              <span className="font-mono text-lg uppercase tracking-widest">Yacht Labs</span>
            </div>
            <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest leading-relaxed">
              Synthesizing the future of digital architecture. Build fast. Ship faster.
            </p>
            <div className="flex gap-4">
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="p-2 border border-zinc-900 rounded-lg text-zinc-500 hover:text-[#0074D9] hover:border-[#0074D9]/50 transition-all">
                <Github size={16} />
              </a>
              <a href="mailto:hello@yacht-labs.com" className="p-2 border border-zinc-900 rounded-lg text-zinc-500 hover:text-[#0074D9] hover:border-[#0074D9]/50 transition-all">
                <Mail size={16} />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Product</h5>
              <div className="flex flex-col gap-2">
                <Link to="/product/synthesis" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Synthesis Engine</Link>
                <Link to="/product/kinetics" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Kinetics Distribution</Link>
                <Link to="/product/workbench" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">The Workbench</Link>
                <Link to="/pricing" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Use Cases</h5>
              <div className="flex flex-col gap-2">
                <Link to="/use-cases/startups" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Founders & Startups</Link>
                <Link to="/use-cases/enterprise" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Enterprise solutions</Link>
                <Link to="/use-cases/agencies" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Creative Agencies</Link>
                <Link to="/solutions" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Our Solutions</Link>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Resources</h5>
              <div className="flex flex-col gap-2">
                <Link to="/resources/learning" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Learning Center</Link>
                <Link to="/resources/api-reference" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">API Reference</Link>
                <Link to="/#support" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Support Center</Link>
                <Link to="/blog" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">The Logbook</Link>
              </div>
            </div>
            <div className="space-y-4">
              <h5 className="text-[10px] font-mono uppercase tracking-widest text-white">Company</h5>
              <div className="flex flex-col gap-2">
                <Link to="/company/team" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Our Team</Link>
                <Link to="/company/vision" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">The Vision</Link>
                <Link to="/company/careers" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Careers</Link>
                <Link to="/about" className="text-[9px] font-mono text-zinc-500 hover:text-white uppercase tracking-widest">About Yacht</Link>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-16 pt-8 border-t border-zinc-900 flex justify-between items-center text-[8px] font-mono uppercase tracking-widest text-zinc-700">
          <span>© 2026 Yacht Labs Inc. / All Rights Reserved.</span>
          <span>Latency: 24ms / Status: Nominal</span>
        </div>
      </footer>
    </div>

  );
}
